import cn from 'classnames';
import { FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import gladCat from '@/shared/assets/images/cats/glad.png';
import { useAppDispatch, useMedia } from '@/shared/hooks';
import { getSupportEmail } from '@/shared/lib';
import { AuthCard, Button, InlineFormError, Input } from '@/shared/ui';

import { authModel } from '@/entities/auth';

import { MFA_CODE_LENGTH, MFA_SUCCESS_HOLD_MS } from '../../config';
import { useMFAErrorMessage } from '../../hooks';
import { MFACodeField } from '../MFACodeField/MFACodeField';

import styles from './MFASignInStep.module.scss';

type SignInStep = 'totp' | 'recovery' | 'recoveryAccepted';

type MFASignInStepProps = {
  className?: string;
  onVerified: () => void;
  onBackToSignIn: () => void;
};

export const MFASignInStep: FC<MFASignInStepProps> = (props) => {
  const { className, onVerified, onBackToSignIn } = props;

  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();
  const dispatch = useAppDispatch();
  const getErrorMessage = useMFAErrorMessage('verify');

  const [step, setStep] = useState<SignInStep>('totp');
  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  // A ref, not the state flag: a click can land in the same tick as the
  // auto-submit on the sixth digit.
  const isVerifyingRef = useRef(false);

  useEffect(() => {
    if (step !== 'recoveryAccepted') {
      return undefined;
    }

    const timeout = setTimeout(onVerified, MFA_SUCCESS_HOLD_MS);

    return () => clearTimeout(timeout);
  }, [step]);

  const verify = async (factor: {
    value: string;
    case: 'TOTPCode' | 'RecoveryCode';
  }) => {
    if (isVerifyingRef.current) {
      return;
    }

    isVerifyingRef.current = true;
    setIsVerifying(true);

    try {
      await dispatch(
        authModel.thunks.mfaSignInVerify({ Factor: factor }),
      ).unwrap();

      if (factor.case === 'RecoveryCode') {
        setStep('recoveryAccepted');
        return;
      }

      onVerified();
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
      setCode('');
    } finally {
      isVerifyingRef.current = false;
      setIsVerifying(false);
    }
  };

  const changeCode = (nextCode: string) => {
    setCode(nextCode);
    setErrorMessage('');
  };

  const openRecoveryStep = () => {
    setStep('recovery');
    setCode('');
    setErrorMessage('');
  };

  const openTOTPStep = () => {
    setStep('totp');
    setRecoveryCode('');
    setErrorMessage('');
  };

  if (step === 'recoveryAccepted') {
    return (
      <AuthCard
        variant="notice"
        className={className}
        dataSection="mfa-recovery-accepted"
      >
        <h3 className={isMobile ? 'h5' : 'h3'}>
          <FormattedMessage
            id="mfa.recovery.acceptedTitle"
            defaultMessage="Two-step verification is off"
          />
        </h3>

        <img
          src={gladCat}
          width={130}
          height={130}
          alt={formatMessage({
            id: 'imgAltText.gladCat',
            defaultMessage: 'Glad cat',
          })}
        />

        <p className={cn(styles.noticeText, 'p2')}>
          <FormattedMessage
            id="mfa.recovery.acceptedDescription"
            defaultMessage="The code has been used up and cannot be used again. Signing you in."
          />
        </p>
      </AuthCard>
    );
  }

  if (step === 'recovery') {
    return (
      <AuthCard
        className={className}
        cardClassName={cn(
          styles.card,
          errorMessage && styles.recoveryCardWithError,
        )}
        dataSection="mfa-recovery-code"
      >
        <div className={styles.intro}>
          <h3 className={isMobile ? 'h5' : 'h3'}>
            <FormattedMessage
              id="mfa.recovery.title"
              defaultMessage="Use a recovery code"
            />
          </h3>

          <p className={cn(styles.description, 'p2')}>
            <FormattedMessage
              id="mfa.recovery.description"
              defaultMessage="Enter one of the ten codes you saved when you set up two-step verification. It will be used up, and two-step verification will be turned off for your account."
            />
          </p>
        </div>

        <Input
          name="recovery-code"
          value={recoveryCode}
          required
          autoFocus
          autoComplete="off"
          placeholder="xxxx-xxxx"
          label={formatMessage({
            id: 'mfa.recovery.codeLabel',
            defaultMessage: 'Recovery code',
          })}
          className={styles.recoveryInput}
          onChange={(event) => {
            setRecoveryCode(event.target.value);
            setErrorMessage('');
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && recoveryCode.trim()) {
              verify({ value: recoveryCode.trim(), case: 'RecoveryCode' });
            }
          }}
        />

        {errorMessage && (
          <InlineFormError
            className={styles.recoveryFormError}
            errorMessage={errorMessage}
          />
        )}

        <div className={styles.recoveryFooter}>
          <p className={cn(styles.supportHint, 'p3')}>
            <FormattedMessage
              id="mfa.recovery.noCodesLeft"
              defaultMessage="No codes left, or lost them too? <link>Contact support</link>"
              values={{
                link: (chunks) => (
                  <a href={`mailto:${getSupportEmail()}`}>{chunks}</a>
                ),
              }}
            />
          </p>

          <div className={styles.actions}>
            <Button
              className={styles.secondaryAction}
              variant="tertiary"
              disabled={isVerifying}
              onClick={openTOTPStep}
            >
              <FormattedMessage
                id="mfa.recovery.useCodeInstead"
                defaultMessage="Enter a code instead"
              />
            </Button>

            <Button
              className={styles.primaryAction}
              loading={isVerifying}
              disabled={!recoveryCode.trim()}
              onClick={() =>
                verify({
                  value: recoveryCode.trim(),
                  case: 'RecoveryCode',
                })
              }
            >
              <FormattedMessage id="global.confirm" defaultMessage="Confirm" />
            </Button>
          </div>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      className={className}
      cardClassName={styles.card}
      dataSection="mfa-sign-in-verify"
    >
      <div className={styles.intro}>
        <h3 className={isMobile ? 'h5' : 'h3'}>
          <FormattedMessage
            id="mfa.verify.title"
            defaultMessage="Two-step verification"
          />
        </h3>

        <p className={cn(styles.description, 'p2')}>
          <FormattedMessage
            id="mfa.verify.description"
            defaultMessage="Enter the 6-digit code from your authenticator app."
          />
        </p>
      </div>

      <MFACodeField
        value={code}
        autoFocus
        disabled={isVerifying}
        errorMessage={errorMessage}
        onChange={changeCode}
        onComplete={(completedCode) =>
          verify({ value: completedCode, case: 'TOTPCode' })
        }
        label={
          <FormattedMessage
            id="mfa.verify.codeLabel"
            defaultMessage="Verification code"
          />
        }
      />

      <div className={styles.footer}>
        <Button
          className={styles.lostAccess}
          variant="tertiary"
          size="medium"
          disabled={isVerifying}
          onClick={openRecoveryStep}
        >
          <FormattedMessage
            id="mfa.verify.lostAccess"
            defaultMessage="Lost access to your authenticator app?"
          />
        </Button>

        <div className={styles.actions}>
          <Button
            className={styles.secondaryAction}
            variant="tertiary"
            disabled={isVerifying}
            onClick={onBackToSignIn}
          >
            <FormattedMessage
              id="mfa.verify.backToSignIn"
              defaultMessage="Back to sign in"
            />
          </Button>

          <Button
            className={styles.primaryAction}
            loading={isVerifying}
            disabled={code.length !== MFA_CODE_LENGTH}
            onClick={() => verify({ value: code, case: 'TOTPCode' })}
          >
            <FormattedMessage id="global.confirm" defaultMessage="Confirm" />
          </Button>
        </div>
      </div>
    </AuthCard>
  );
};
